import React, { useMemo, useState } from "react";
import { Box, Typography, IconButton, Dialog, DialogTitle, DialogContent } from "@mui/material";
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import CloseIcon from "@mui/icons-material/Close";
import { exportToExcel, ExcelData } from "../../../utils/excelExport";
import styles from "./horizontalBarChart.module.scss";

interface HorizontalBarChartProps {
  title?: string;
  data?: any[];
  nameKey?: string;
  valueKey?: string;
  barColor?: string;
  colorKey?: string;
  height?: number;
  valueLabel?: string;
  yAxisWidth?: number;
  maxLabelLength?: number;
  itemsPerPage?: number;
  onDownload?: () => void;
  excelFileName?: string;
}

const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
  title = "Horizontal Bar Chart",
  data = [],
  nameKey = "name",
  valueKey = "value",
  barColor = "#5b6b9e",
  colorKey,
  height,
  valueLabel = "",
  yAxisWidth = 320,
  maxLabelLength = 40,
  itemsPerPage,
  onDownload,
  excelFileName = "Horizontal_Bar_Chart_Data",
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleExpandClick = () => {
    setIsExpanded(true);
  };

  const handleCloseExpand = () => {
    setIsExpanded(false);
  };
  const handleDownload = () => {
    if (onDownload) {
      onDownload();
      return;
    }

    const excelData: ExcelData = {
      sheetName: title,
      fileName: title.replace(/\s+/g, "_"),
      columns: [
        { header: "Entity", key: "name", width: 50 },
        { header: `Value ${valueLabel ? `(${valueLabel})` : ""}`, key: "value", width: 20 },
      ],
      data: allChartData.map((item) => ({
        name: item.name,
        value: item.value,
      })),
    };

    exportToExcel(excelData);
  };
  // Transform and normalize data
  const allChartData = useMemo(() => {
    if (!data || data.length === 0) return [];

    return data.map((item, index) => {
      const fullName =
        item[nameKey] ||
        item.group ||
        item.name ||
        item.label ||
        `Item ${index + 1}`;

      return {
        name: fullName,
        displayName:
          fullName.length > maxLabelLength
            ? `${fullName.substring(0, maxLabelLength)}...`
            : fullName,
        value: Number(item[valueKey] || item.value || 0),
        color: colorKey ? item[colorKey] : barColor,
      };
    });
  }, [data, nameKey, valueKey, colorKey, barColor, maxLabelLength]);

  // Display data - show limited items if itemsPerPage is provided
  const chartData = useMemo(() => {
    if (itemsPerPage && allChartData.length > itemsPerPage) {
      return allChartData.slice(0, itemsPerPage);
    }
    return allChartData;
  }, [allChartData, itemsPerPage]);

  // Calculate dynamic height based on number of items
  const dynamicHeight = useMemo(() => {
    const itemCount = chartData.length;
    const minHeight = 300;
    const heightPerItem = 45; // Compact spacing per bar
    const calculatedHeight = Math.max(minHeight, itemCount * heightPerItem);

    // Use provided height only if it's larger than calculated height
    return height && height > calculatedHeight ? height : calculatedHeight;
  }, [chartData.length, height]);

  const expandedHeight = useMemo(() => {
    const itemCount = allChartData.length;
    const minHeight = 300;
    const heightPerItem = 45;
    const calculatedHeight = Math.max(minHeight, itemCount * heightPerItem);

    return height && height > calculatedHeight ? height : calculatedHeight;
  }, [allChartData.length, height]);

  // Calculate dynamic domain and ticks based on data
  const { maxValue, ticks } = useMemo(() => {
    if (!allChartData || allChartData.length === 0) {
      return { maxValue: 100, ticks: [0, 25, 50, 75, 100] };
    }

    const max = Math.max(...allChartData.map((item) => item.value), 1);

    // Calculate nice interval based on magnitude
    const magnitude = Math.pow(10, Math.floor(Math.log10(max)));
    const normalized = max / magnitude;

    // Determine tick interval based on normalized value
    let tickInterval;
    if (normalized <= 1.5) tickInterval = 0.2 * magnitude;
    else if (normalized <= 3) tickInterval = 0.5 * magnitude;
    else if (normalized <= 7) tickInterval = 1 * magnitude;
    else tickInterval = 2 * magnitude;

    // Calculate max value as next multiple of tickInterval
    const roundedMax = Math.ceil(max / tickInterval) * tickInterval;

    // Generate 5 evenly spaced ticks
    const calculatedTicks = Array.from(
      { length: 5 },
      (_, i) => (i * roundedMax) / 4,
    );

    return { maxValue: roundedMax, ticks: calculatedTicks };
  }, [allChartData]);

  // Format large numbers for better readability
  const formatAxisValue = (value: number): string => {
    if (value >= 1000000) {
      const millions = value / 1000000;
      return millions % 1 === 0 ? `${millions.toFixed(0)}M` : `${millions.toFixed(1)}M`;
    }
    if (value >= 1000) {
      const thousands = value / 1000;
      return thousands % 1 === 0 ? `${thousands.toFixed(0)}K` : `${thousands.toFixed(1)}K`;
    }
    return value.toString();
  };

  const formatTooltipValue = (value: number): string => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(1)}K`;
    return value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box className={styles.customTooltip}>
          <Typography className={styles.tooltipLabel}>
            {payload[0].payload.name}
          </Typography>
          <Typography className={styles.tooltipValue}>
            {formatTooltipValue(payload[0].value)} {valueLabel}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  if (!allChartData || allChartData.length === 0) {
    return (
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
        </Box>
        <Box
          className={styles.chartContainer}
          display="flex"
          alignItems="center"
          justifyContent="center"
          minHeight={300}
        >
          <Typography variant="body2" color="textSecondary">
            No data available
          </Typography>
        </Box>
      </Box>
    );
  }

  const renderBarChart = (data: typeof chartData, height: number, isModal = false) => (
    <Box className={isModal ? styles.modalChartContainer : styles.chartContainer}>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={data}
          layout="vertical"
          margin={{ top: 10, right: 30, left: 0, bottom: 10 }}
          barCategoryGap={8}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            type="number"
            tick={{ fill: "#666666", fontSize: 12 }}
            axisLine={{ stroke: "#e0e0e0" }}
            domain={[0, maxValue]}
            ticks={ticks}
            tickFormatter={formatAxisValue}
          />
          <YAxis
            type="category"
            dataKey="displayName"
            tick={{ fill: "#666666", fontSize: 12 }}
            axisLine={{ stroke: "#e0e0e0" }}
            width={yAxisWidth}
            interval={0}
          />
          <Tooltip
            content={<CustomTooltip />}
            cursor={{ fill: "rgba(0, 0, 0, 0.05)" }}
          />
          <Bar
            dataKey="value"
            fill={barColor}
            radius={[0, 4, 4, 0]}
            barSize={32}
          >
            {colorKey &&
              data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );

  return (
    <>
      <Box className={styles.container}>
        <Box className={styles.header}>
          <Typography variant="h6" className={styles.title}>
            {title}
          </Typography>
          <Box className={styles.headerActions}>
            {itemsPerPage && allChartData.length > itemsPerPage && (
              <IconButton
                onClick={handleExpandClick}
                size="small"
                sx={{
                  color: "#5b6b9e",
                  "&:hover": {
                    backgroundColor: "rgba(91, 107, 158, 0.1)",
                  },
                }}
                aria-label="expand"
              >
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              onClick={handleDownload}
              size="small"
              sx={{
                color: "#5b6b9e",
                "&:hover": {
                  backgroundColor: "rgba(91, 107, 158, 0.1)",
                },
              }}
              aria-label="download"
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {renderBarChart(chartData, dynamicHeight)}
      </Box>

      <Dialog
        open={isExpanded}
        onClose={handleCloseExpand}
        maxWidth={false}
        fullScreen
        className={styles.expandDialog}
      >
        <DialogTitle className={styles.dialogTitle}>
          <Typography variant="h6" className={styles.dialogTitleText}>
            {title}
          </Typography>
          <Box className={styles.dialogActions}>
            <IconButton
              onClick={handleDownload}
              size="small"
              sx={{
                color: "#5b6b9e",
                "&:hover": {
                  backgroundColor: "rgba(91, 107, 158, 0.1)",
                },
              }}
              aria-label="download"
            >
              <FileDownloadOutlinedIcon fontSize="small" />
            </IconButton>
            <IconButton
              aria-label="close"
              onClick={handleCloseExpand}
              sx={{
                color: "#5b6b9e",
                "&:hover": {
                  backgroundColor: "rgba(91, 107, 158, 0.1)",
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent className={styles.dialogContent}>
          {renderBarChart(allChartData, expandedHeight, true)}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HorizontalBarChart;
